<template>
  <div
    v-if="initialized"
    class="small cascade-column-select-panel--wrapper"
    ref="cascaderPanelWrapper"
    style="overflow: scroll; width: 180px"
  >
    <el-cascader
      ref="elCascader"
      style="max-width: 98vw; overflow: scroll"
      placeholder="項目名で検索..."
      :props="cascaderProps"
      :filterable="true"
      :filter-method="
        (node, keyword) => {
          // @ts-ignore
          return node.value?.indexOf?.(keyword) >= 0 || node.label.indexOf(keyword) >= 0
        }
      "
      @change="handleChange"
      @expand-change="() => $emit('expand-change')"
    />
  </div>
</template>

<script lang="ts">
import { ElCascader } from 'element-plus'
import { inject, PropType } from 'vue'
import { ColumnDef, ColumnDefByColName } from '../../../../../common/$models/ModelDef'
import { FilterControlsService } from '../../FilterControlsService'
import {
  createNestedColumnSelectionLazyloadFunction,
  NestedColumnSelectionSelectionsConvertFunction,
} from '../../createNestedColSelectionLazyloadFunction'
/**
 * カラム選択用のカスケードパネルコンポーネント
 *
 * @description
 * このコンポーネントは、ModelDefのカラム定義に基づいて、ネストされたカラム選択UIを提供します。
 * 主にフィルター条件の設定や、カラム選択が必要な場面で使用されます。
 *
 * @features
 * - カラム検索機能
 * - カスケード選択（ネストされたカラムの選択）
 * - 指定カラムのフィルタリング
 * - FilterControlsServiceとの連携（オプショナル）
 *
 * @example
 * ```vue
 * <template>
 *   <!-- モデル名を指定して、対象となるカラムを指定する -->
 *   <CascadeColumnSelectPanel
 *     :model-name="'selectOptionsMaster'"
 *     @column-selected="(colNamePath) => console.log('Selected column path:', colNamePath)"
 *   />
 *   <!-- カラム定義を直接指定する -->
 *   <CascadeColumnSelectPanel
 *     :columns-by-col-name="columnsByColName"
 *     @column-selected="(colNamePath) => console.log('Selected column path:', colNamePath)"
 *   />
 *   <!-- FilterControlsService (inject) を利用する場合 -->
 *   <CascadeColumnSelectPanel
 *     @column-selected="(colNamePath) => console.log('Selected column path:', colNamePath)"
 *   />
 * </template>
 * ```
 *
 * @props
 * - specifiedColNames (string[]): 選択可能なカラム名を制限する場合に指定
 * - onColumnSelect (Function): カラム選択時のコールバック関数
 *
 * @emits
 * - column-selected: カラムが選択された時に発火
 * - search-changed: 検索文字列が変更された時に発火
 *
 * @dependencies
 * - FilterControlsService (オプショナル): provide/injectで注入可能
 * - element-plus: ElCascaderPanelコンポーネントを使用
 *
 * @usage
 * 1. 基本的な使用方法:
 *    - フィルター条件の設定画面でカラム選択用に使用
 *    - フォーム内でのカラム選択UIとして使用
 *
 * 2. FilterControlsServiceと組み合わせた使用:
 *    - FilterControlsServiceをprovideして使用
 *    - カラム定義やフィルター状態の管理を自動化
 *
 * 3. スタンドアロンでの使用:
 *    - FilterControlsServiceなしでも使用可能
 *    - 必要なカラム定義は props `modelName` または `columnsByColName` にて 設定する
 */
export default {
  name: 'CascadeColumnSelectPanel',
  components: {
    ElCascader,
  },
  props: {
    specifiedColNames: {
      type: Array as PropType<string[]>,
      default: () => [],
      required: false,
    },
    onColumnSelect: {
      type: Function as PropType<(colNamePaths: string[]) => void>,
      required: false,
    },
    /**
     * モデル名を指定して、対象となるカラムを指定する
     * ※ FilterControlsServiceInstance が 存在しないときに利用
     */
    modelName: {
      type: String,
      required: false,
    },
    /**
     * カラム定義を直接指定する
     * ※ FilterControlsServiceInstance が 存在しないときに利用
     */
    columnsByColName: {
      type: Object as PropType<{ [key: string]: ColumnDef }>,
      default: null,
    },
    enableKeywordSearch: {
      type: Boolean,
      default: true,
    },
    /**
     * ネストされたカラム選択肢 を filtering したい場合に 使用する
     */
    selectionsConvertFunction: {
      type: Function as PropType<NestedColumnSelectionSelectionsConvertFunction>,
      default: null,
    },
    maxNestedLevel: {
      type: Number,
      default: null,
    },
    height: {
      type: String,
      default: '400px',
    },
    maxHeight: {
      type: String,
      default: '400px',
    },
  },
  emits: ['column-selected', 'search-changed', 'expand-change'],
  setup() {
    return {
      FilterControlsServiceInstance: inject<FilterControlsService>(
        'FilterControlsServiceInstance',
        {
          columns: {},
        } as FilterControlsService,
      ),
    }
  },
  data() {
    return {
      value: [],
      initialized: false,
    }
  },
  computed: {
    cascaderProps() {
      // カラム選択のための props, nest の箇所は 選択時に 展開できる 構造になっている by lazyLoad function
      return {
        lazy: true,
        lazyLoad: this.cascaderLazyloadFunction,
      }
    },
    cascaderLazyloadFunction() {
      return createNestedColumnSelectionLazyloadFunction(this.selectableColumns, {
        maxNestedLevel: this.maxNestedLevel,
        selectionsConvertFunction: this.selectionsConvertFunction,
      })
    },
    columns(): ColumnDefByColName {
      if (this.columnsByColName) {
        return this.columnsByColName
      }
      if (this.modelName) {
        return $core.$models[this.modelName].columns
      }
      if (this.FilterControlsServiceInstance?.columns) {
        return this.FilterControlsServiceInstance.columns
      }
      return {}
    },
    selectableColumns(): ColumnDefByColName {
      if (this.specifiedColNames?.length > 0) {
        return this.specifiedColNames.reduce((acc, colName) => {
          acc[colName] = this.columns[colName]
          return acc
        }, {})
      }
      return this.columns || {}
    },
  },
  mounted() {
    if (
      !this.FilterControlsServiceInstance &&
      !this.modelName &&
      !this.columnsByColName
    ) {
      console.error(
        '[CascadeColumnSelectPanel] FilterControlsServiceInstance が 設定されていない場合には、modelName または columnsByColName を設定してください。',
      )
      return
    }
    this.$nextTick(() => {
      this.initialized = true
      this.$nextTick(() => {
        setTimeout(() => {
          // @ts-ignore
          this.$refs.elCascader?.togglePopperVisible?.(true)
        }, 100)
      })
    })
  },
  beforeUnmount() {
    if (this.$refs.elCascaderPanel) {
      this.$refs.elCascaderPanel = null
    }
  },
  methods: {
    /**
     * カラムが選択された場合に kick される。
     * - Nest した 選択である場合があるため、ColumnName の 配列で渡される。
     */
    handleChange(selectedColumnPaths: string[]) {
      this.$emit('column-selected', selectedColumnPaths)
      this.onColumnSelect?.(selectedColumnPaths)
      this.value = []
    },
  },
}
</script>
